import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export function useLikes() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['likes', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('likes')
        .select(`
          *,
          products (*)
        `)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }
      return data || [];
    },
    enabled: !!user,
  });

  const toggleLike = useMutation({
    mutationFn: async (productId: string) => {
      if (!user) throw new Error('Must be logged in to like products');

      // Check if product is already liked
      const { data: existingLike, error: checkError } = await supabase
        .from('likes')
        .select('id')
        .eq('user_id', user.id)
        .eq('product_id', productId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingLike) {
        // Unlike
        const { error } = await supabase
          .from('likes')
          .delete()
          .eq('id', existingLike.id);

        if (error) {
          throw error;
        }
      } else {
        // Like
        const { error } = await supabase
          .from('likes')
          .insert({
            user_id: user.id,
            product_id: productId
          });

        if (error) {
          throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['likes', user?.id] });
    },
  });

  const isLiked = (productId: string) => {
    return query.data?.some(like => like.product_id === productId) || false;
  };

  return {
    likes: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    toggleLike: toggleLike.mutate,
    isLiked,
  };
} 